/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Lookup table usage", description = "Understand the output tables Identity Resolution writes to your warehouse and how to use them.", lastUpdatedDate = new Date(1789430400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Lookup table usage",
        description: "Understand the output tables Identity Resolution writes to your warehouse and how to use them.",
        lastUpdatedDate: "Tue Sep 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Where identity outputs live",
          "depth": 0,
          "id": "where-identity-outputs-live"
        }, {
          "value": "Core output tables",
          "depth": 0,
          "id": "core-output-tables"
        }, {
          "value": "Resolved table",
          "depth": 1,
          "id": "resolved-table"
        }, {
          "value": "Resolved identifiers table",
          "depth": 1,
          "id": "resolved-identifiers-table"
        }, {
          "value": "Unresolved table",
          "depth": 1,
          "id": "unresolved-table"
        }, {
          "value": "Golden Record table",
          "depth": 1,
          "id": "golden-record-table"
        }, {
          "value": "State tables",
          "depth": 1,
          "id": "state-tables"
        }, {
          "value": "Common usage patterns",
          "depth": 0,
          "id": "common-usage-patterns"
        }, {
          "value": "Analyze data at the identity level",
          "depth": 1,
          "id": "analyze-data-at-the-identity-level"
        }, {
          "value": "Use Golden Record for analytics and reporting",
          "depth": 1,
          "id": "use-golden-record-for-analytics-and-reporting"
        }, {
          "value": "Retrieve identifiers for activation",
          "depth": 1,
          "id": "retrieve-identifiers-for-activation"
        }, {
          "value": "Using identity data in Customer Studio",
          "depth": 0,
          "id": "using-identity-data-in-customer-studio"
        }, {
          "value": "Sync and activation guidance",
          "depth": 0,
          "id": "sync-and-activation-guidance"
        }, {
          "value": "Using ht_id as a sync key",
          "depth": 1,
          "id": "using-ht_id-as-a-sync-key"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      code: "code",
      ul: "ul",
      li: "li",
      hr: "hr",
      h3: "h3",
      h4: "h4",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      pre: "pre",
      span: "span",
      a: "a",
      br: "br"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Identity resolution",
        extraText: ". You can use it with or without Customer Studio."
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["After an identity graph runs, Identity Resolution writes a set of ", _jsx(_components.strong, {
          children: "output tables"
        }), " to your data warehouse. These tables represent the current state of your identity graph and are designed for downstream use across analytics, activation, and Customer Studio."]
      }), "\n", _jsx(_components.p, {
        children: "Your marketing team can use these tables for paid campaigns, your sales team for deduplicating outreach efforts, and your data science team for fraud detection."
      }), "\n", _jsx(_components.h2, {
        id: "where-identity-outputs-live",
        children: "Where identity outputs live"
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution writes output tables to your warehouse, typically in the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema (or a schema override configured for your source)."]
      }), "\n", _jsx(_components.p, {
        children: "These tables act as stable integration points. You can use them to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Query them directly in SQL"
        }), "\n", _jsx(_components.li, {
          children: "Register them as dbt sources"
        }), "\n", _jsx(_components.li, {
          children: "Use them to build analytics models or views"
        }), "\n", _jsx(_components.li, {
          children: "Use them as the foundation for Customer Studio schemas"
        }), "\n", _jsx(_components.li, {
          children: "Sync from them to downstream tools"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["While the ", _jsx(_components.code, {
            children: "hightouch_planner"
          }), " schema is generally managed internally by Hightouch, ", _jsx(_components.strong, {
            children: "the Identity Resolution output tables described on this page are intentionally created for your use."
          }), " You can safely query them directly, reference them in downstream ETL pipelines, or register them as dbt sources. For long-term stability, we recommend creating a view or derived table that references these outputs rather than transforming them in place."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-output-tables",
        children: "Core output tables"
      }), "\n", _jsx(_components.p, {
        children: "Each identity graph produces the following output tables."
      }), "\n", _jsx(_components.h3, {
        id: "resolved-table",
        children: "Resolved table"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "<output_prefix>_resolved"
        })
      }), "\n", _jsx(_components.p, {
        children: "This table maps every input row used in Identity Resolution to a resolved identity."
      }), "\n", _jsx(_components.p, {
        children: "Each row represents a single input record and includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ht_id"
          }), ": the resolved identity ID"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "source"
          }), ": the originating model"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "primary_key"
          }), ": the primary key value of the input record"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "latest_timestamp"
          }), ": the most recent timestamp for that record"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use this table when you need to:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Join raw source data to resolved identities"
        }), "\n", _jsx(_components.li, {
          children: "Aggregate metrics at the identity level"
        }), "\n", _jsx(_components.li, {
          children: "Trace how specific records were grouped into profiles"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "example-output",
        children: "Example output"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "HT_ID"
            }), _jsx(_components.th, {
              children: "SOURCE"
            }), _jsx(_components.th, {
              children: "PRIMARY_KEY"
            }), _jsx(_components.th, {
              children: "LATEST_TIMESTAMP"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:00:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:05:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:10:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "4"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:15:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "5"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:20:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "6"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:25:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "7"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:30:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "8"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:35:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht9"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "9"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:40:00.000"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht9"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "10"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:45:00.000"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "resolved-identifiers-table",
        children: "Resolved identifiers table"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "<output_prefix>_resolved_identifiers"
        })
      }), "\n", _jsx(_components.p, {
        children: "This table contains all identifier values associated with each resolved identity."
      }), "\n", _jsx(_components.p, {
        children: "Each row represents a single identifier value observed for that identity, along with when and where it appeared."
      }), "\n", _jsx(_components.p, {
        children: "Each row includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ht_id"
          }), ": the resolved identity ID"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "identifier"
          }), ": the identifier type (for example, ", _jsx(_components.code, {
            children: "email"
          }), " or ", _jsx(_components.code, {
            children: "user_id"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "value"
          }), ": the identifier value"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "first_timestamp"
          }), " and ", _jsx(_components.code, {
            children: "last_timestamp"
          }), ": when the value was first and last seen"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "count"
          }), ": how many times the value appeared across input records"]
        }), "\n", _jsxs(_components.li, {
          children: ["Lineage fields identifying the originating record and model (", _jsx(_components.code, {
            children: "initial_row_pk"
          }), ", ", _jsx(_components.code, {
            children: "initial_row_source"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Use this table when you need to:"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Retrieve the full set of identifiers associated with an identity"
        }), "\n", _jsx(_components.li, {
          children: "Sync multiple identifiers per identity to downstream destinations"
        }), "\n", _jsx(_components.li, {
          children: "Inspect how specific identifiers contributed to identity resolution"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "example-output-1",
        children: "Example output"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "HT_ID"
            }), _jsx(_components.th, {
              children: "IDENTIFIER"
            }), _jsx(_components.th, {
              children: "VALUE"
            }), _jsx(_components.th, {
              children: "INITIAL_ROW_PK"
            }), _jsx(_components.th, {
              children: "INITIAL_ROW_SOURCE"
            }), _jsx(_components.th, {
              children: "FIRST_TIMESTAMP"
            }), _jsx(_components.th, {
              children: "LAST_TIMESTAMP"
            }), _jsx(_components.th, {
              children: "COUNT"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "user_id"
            }), _jsx(_components.td, {
              children: "user_003"
            }), _jsx(_components.td, {
              children: "4"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:15:00.000"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:15:00.000"
            }), _jsx(_components.td, {
              children: "1"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht1"
            }), _jsx(_components.td, {
              children: "anonymous_id"
            }), _jsx(_components.td, {
              children: "anon_125"
            }), _jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:00:00.000"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:15:00.000"
            }), _jsx(_components.td, {
              children: "4"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "user_id"
            }), _jsx(_components.td, {
              children: "user_004"
            }), _jsx(_components.td, {
              children: "8"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:35:00.000"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:35:00.000"
            }), _jsx(_components.td, {
              children: "1"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht5"
            }), _jsx(_components.td, {
              children: "anonymous_id"
            }), _jsx(_components.td, {
              children: "anon_126"
            }), _jsx(_components.td, {
              children: "5"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:20:00.000"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:35:00.000"
            }), _jsx(_components.td, {
              children: "4"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht9"
            }), _jsx(_components.td, {
              children: "anonymous_id"
            }), _jsx(_components.td, {
              children: "anon_127"
            }), _jsx(_components.td, {
              children: "9"
            }), _jsx(_components.td, {
              children: "earl-s-ecommerce-events"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:40:00.000"
            }), _jsx(_components.td, {
              children: "2024-08-01 12:45:00.000"
            }), _jsx(_components.td, {
              children: "2"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "unresolved-table",
        children: "Unresolved table"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "<output_prefix>_unresolved"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This table contains input rows that could not be processed, most commonly because ", _jsx(_components.strong, {
          children: "primary key values were not unique"
        }), " within the originating model."]
      }), "\n", _jsx(_components.p, {
        children: "Use this table to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Debug data quality issues"
        }), "\n", _jsx(_components.li, {
          children: "Identify records excluded from identity resolution"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "golden-record-table",
        children: "Golden Record table"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "<output_prefix>_golden_records"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution produces a flattened Golden Record table with ", _jsx(_components.strong, {
          children: "one row per resolved identity"
        }), ". This table selects a single, trusted value for each field based on the survivorship rules you configure, creating a marketer-friendly profile representation for downstream use."]
      }), "\n", _jsx(_components.p, {
        children: "Each row includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ht_id"
          }), ", which uniquely identifies the resolved identity"]
        }), "\n", _jsx(_components.li, {
          children: "One column per field configured in Golden Record"
        }), "\n", _jsx(_components.li, {
          children: "Values selected using survivorship rules such as recency, frequency, source priority, or array"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Golden Record is the most common starting point for analytics and activation because it provides a clean, one-row-per-identity view with canonical fields."
      }), "\n", _jsx(_components.h4, {
        id: "example-row",
        children: "Example row"
      }), "\n", _jsxs(_components.p, {
        children: ["Each row corresponds to a single ", _jsx(_components.code, {
          children: "HT_ID"
        }), " (one identity). For example, a row might look like:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: ["{\n  \"ht_id\": \"ht1\",\n  \"most_recent_email\": \"user@example.com\",                ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- using most recent survivorship"
          }), "\n  \"all_phone_numbers\": [\"123-456-7890\", \"098-765-4321\"],  ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- using array survivorship"
          }), "\n  \"primary_user_id\": \"user_123\"                           ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- using source priority survivorship"
          }), "\n}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For configuration and survivorship rules, see ", _jsx(_components.a, {
          href: "/identity-resolution/golden-record",
          children: "Golden Record"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
        id: "state-tables",
        children: "State tables"
      }), "\n", _jsxs(_components.p, {
        children: ["Identity Resolution also creates internal ", _jsx(_components.strong, {
          children: "state tables"
        }), "—typically prefixed with ", _jsx(_components.code, {
          children: "IDR_..."
        }), " or ", _jsx(_components.code, {
          children: "IDR_BACKUP_..."
        }), "—to support incremental processing and maintain graph consistency in the same schema."]
      }), "\n", _jsx(_components.p, {
        children: "These tables are managed by Hightouch and should not be queried or modified directly."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Only the ", _jsx(_components.strong, {
            children: "output tables"
          }), " (for example, ", _jsx(_components.code, {
            children: "<output_prefix>_resolved"
          }), ", ", _jsx(_components.code, {
            children: "<output_prefix>_resolved_identifiers"
          }), ", ", _jsx(_components.code, {
            children: "<output_prefix>_unresolved"
          }), ", and ", _jsx(_components.code, {
            children: "<output_prefix>_golden_records"
          }), ") are intended for external use. Treat state tables as internal implementation details."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "common-usage-patterns",
        children: "Common usage patterns"
      }), "\n", _jsx(_components.h3, {
        id: "analyze-data-at-the-identity-level",
        children: "Analyze data at the identity level"
      }), "\n", _jsxs(_components.p, {
        children: ["A common pattern is to aggregate activity or metrics by ", _jsx(_components.code, {
          children: "ht_id"
        }), " rather than by raw records."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can do this by joining source tables (such as events or transactions) to the ", _jsx(_components.strong, {
          children: "resolved"
        }), " table and grouping by ", _jsx(_components.code, {
          children: "ht_id"
        }), ". This lets you analyze behavior, engagement, or revenue at the resolved identity level instead of the raw‑record level."]
      }), "\n", _jsx(_components.h3, {
        id: "use-golden-record-for-analytics-and-reporting",
        children: "Use Golden Record for analytics and reporting"
      }), "\n", _jsx(_components.p, {
        children: "Golden Record is typically used as the primary table for analytics and Customer 360‑style views."
      }), "\n", _jsx(_components.p, {
        children: "Patterns include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Starting from ", _jsx(_components.code, {
            children: "<output_prefix>_golden_records"
          }), " as the base table"]
        }), "\n", _jsxs(_components.li, {
          children: ["Joining transactional data (such as orders or subscriptions) using ", _jsx(_components.code, {
            children: "ht_id"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Aggregating metrics like lifetime value per resolved identity"
        }), "\n", _jsx(_components.li, {
          children: "Using Golden Record fields (such as primary email) as trusted attributes"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "retrieve-identifiers-for-activation",
        children: "Retrieve identifiers for activation"
      }), "\n", _jsx(_components.p, {
        children: "When syncing to destinations that support multiple identifiers per user (such as ad platforms), you may want access to all identifier values for each resolved identity."
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.code, {
          children: "<output_prefix>_resolved_identifiers"
        }), " to retrieve and group identifier values by ", _jsx(_components.code, {
          children: "ht_id"
        }), ". This supports sending multiple emails, phone numbers, or other identifiers per identity when required by the destination."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "using-identity-data-in-customer-studio",
        children: "Using identity data in Customer Studio"
      }), "\n", _jsx(_components.p, {
        children: "If you use Customer Studio, identity outputs are typically used in one of two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Golden Record as the parent model"
            }), _jsx(_components.br, {}), "\n", "A unified, one‑row‑per‑identity table that marketers can build audiences and traits from directly."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Resolved records as related models"
            }), _jsx(_components.br, {}), "\n", "Events or transactions roll up to identities using ", _jsx(_components.code, {
              children: "ht_id"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "This structure lets you build audiences, traits, and insights on unified customer data without writing SQL."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "sync-and-activation-guidance",
        children: "Sync and activation guidance"
      }), "\n", _jsx(_components.p, {
        children: "When syncing identity data to downstream tools:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Prefer syncing from ", _jsx(_components.strong, {
            children: "Golden Record"
          }), " when the destination expects ", _jsx(_components.strong, {
            children: "one record per person"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use ", _jsx(_components.strong, {
            children: "resolved identifiers"
          }), " when a destination supports multiple identifiers."]
        }), "\n", _jsx(_components.li, {
          children: "Always structure syncs based on the destination's data model and requirements."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "using-ht_id-as-a-sync-key",
        children: "Using ht_id as a sync key"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "ht_id"
        }), " identifies a profile, not a person. Ids persist, but profiles may merge or split as the graph learns, and the id follows the records. For a key that never changes, anchor on a real-world identifier such as ", _jsx(_components.code, {
          children: "user_id"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", or an ", _jsx(_components.code, {
          children: "external_id"
        }), ", and let Hightouch resolve identifiers underneath it. See ", _jsx(_components.a, {
          href: "/identity-resolution/how-it-works#full-re-runs",
          children: "Full re-runs"
        }), " for exactly when an ", _jsx(_components.code, {
          children: "ht_id"
        }), " changes."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
